import React, { useCallback, useContext, useMemo } from 'react'
import styled from 'styled-components'
import { rem } from 'polished'
import cn from 'clsx'
import { mediaQueryUp } from 'components/utils/breakpoint'
import { EmptyMap, groupBy, min } from 'lib/array/arrayUtils'
import BodyText from 'components/Luxkit/Typography/BodyText'
import Caption from 'components/Luxkit/Typography/Caption'
import FormatCurrency from 'components/Common/FormatCurrency'
import {
  checkPurchasableOptionHasPackageUpgradeOrRoomType,
  formatRoomType,
} from 'lib/tours/tourUtils'
import Heading from 'components/Luxkit/Typography/Heading'
import TextButton from 'components/Luxkit/Button/TextButton'
import useTourV2DepartureDetails from 'hooks/TourV2/useTourV2DepartureDetails'
import PricingBreakdownDropdown from './PricingBreakdownDropdown'
import { getDepositAmountPercentage } from 'lib/payment/depositsUtils'
import withOfferPageState from 'contexts/OfferPage/withOfferPageState'
import CurrencyContext from 'contexts/currencyContext'
import SecureWithDepositTagAndModal from 'components/Common/SecureWithDeposit/SecureWithDepositTagAndModal'
import LineSunsetIcon from 'components/Luxkit/Icons/line/LineSunsetIcon'
import useTourV2PurchasableOptionDetails from 'hooks/TourV2/useTourV2PurchasableOptionDetails'
import isTourV2DepositAllowed from 'lib/tours/isTourV2DepositAllowed'
import StatusLabel from 'components/Luxkit/Label/StatusLabel'
import Clickable from 'components/Common/Clickable'
import Group from 'components/utils/Group'
import { getDefaultDepositAmountPercentage } from 'checkout/selectors/featureConfig/deposit'
import { fireInteractionEvent } from 'api/googleTagManager'
import { tourv2DepartureSelect } from 'analytics/eventDefinitions'
import { connect } from 'react-redux'
import LineRoomUpgradeIcon from 'components/Luxkit/Icons/line/LineRoomUpgradeIcon'
import LineBedDoubleAltIcon from 'components/Luxkit/Icons/line/LineBedDoubleAltIcon'
import PromotionLabel from 'components/Luxkit/Label/PromotionLabel'
import PriceRowPrice from 'components/Luxkit/PricePoints/PriceRowPrice'
import PriceRowPriceCaption from 'components/Luxkit/PricePoints/PriceRowPriceCaption'
import TourV2DepartureCta from './TourV2DepartureCta'
import PriceRowSale from 'components/Luxkit/PricePoints/PriceRowSale'
import { pluralizeToString } from 'lib/string/pluralize'
import useTourDepartureRoomInventory from 'hooks/TourV2/useTourDepartureRoomInventory'
import TourV2UrgencyTags from 'components/Tours/TourV2UrgencyTags'
import TourDiscountLabel from 'components/Tours/TourDiscountLabel'
import PriceRowValueDiscountWithCaption from 'components/Luxkit/PricePoints/Value/PriceRowValueDiscountWithCaption'
import UrgencyLabel from 'components/Luxkit/Label/UrgencyLabel'
import PriceRowCaption from 'components/Luxkit/PricePoints/PriceRowCaption'
import PriceRowAgentHubCommission from 'agentHub/components/PriceRowAgentHubCommission'

const Card = styled.div`
  background-color: ${props => props.theme.palette.neutral.default.eight};
  border: 1px solid ${props => props.theme.palette.neutral.default.five};
  box-shadow: none;
  position: relative;
  transition: .2s ease background-color, .2s ease box-shadow;

  &:not(.is-unavailable):hover {
    box-shadow: ${({ theme }) => theme.shadow.bottom.small};
  }

  &.is-unavailable {
    background: ${({ theme }) => theme.palette.neutral.default.seven};
  }

  &.is-selected {
    border-color: ${props => props.theme.palette.highlight.secondary.normal};
    border-width: 2px;
    box-shadow: ${({ theme }) => theme.shadow.bottom.large};
  }
`

const CardBody = styled.div`
  align-items: center;
  display: grid;
  grid-row-gap: ${rem(16)};
  grid-template-areas:
    "details details"
    "pricing buttons";
  grid-template-columns: 1fr min-content;
  min-height: ${rem(135)};
  padding: ${rem(20)} ${rem(16)};

  ${mediaQueryUp.tablet} {
    grid-column-gap: ${rem(20)};
    grid-row-gap: unset;
    grid-template-areas: "details divider pricing buttons";
    grid-template-columns: 1fr min-content ${rem(224)} minmax(0, ${rem(150)});
    padding: ${rem(24)} ${rem(32)} ${rem(16)};
  }

  &.is-unavailable {
    grid-template-columns: 1fr min-content;
    grid-template-areas:
      "details"
      "pricing";

    ${mediaQueryUp.tablet} {
      grid-column-gap: ${rem(20)};
      grid-row-gap: unset;
      grid-template-columns: 1fr min-content ${rem(394)};
      grid-template-areas: "details divider pricing";
    }
  }
`

const DetailsGroup = styled(Group)`
  flex-grow: 1;
  grid-area: details;
`

const JourneyDetailContainer = styled.div`
  align-items: center;
  display: grid;
  grid-column-gap: ${rem(16)};
  grid-template-areas: "start length end";
  grid-template-columns: auto 1fr auto;

  ${mediaQueryUp.tablet} {
    grid-column-gap: ${rem(20)};
  }
`

const JourneyDetailStartContainer = styled.div`
  grid-area: start;
`

const JourneyDetailEndContainer = styled.div`
  grid-area: end;
`

const JourneyDetailLengthContainer = styled.div`
  align-items: center;
  display: grid;
  grid-area: length;
  grid-row-gap: ${rem(8)};
  grid-template-areas: "top" "divider" "bottom";
  grid-template-rows: 1fr min-content 1fr;
  justify-self: center;
  width: 100%;
`

const DaysCountCaption = styled(Caption)`
  grid-area: bottom;
  text-align: center;
`

const JourneyDetialContainerHDivider = styled.hr`
  border: none;
  border-top: 1px solid ${({ theme }) => theme.palette.neutral.default.one};
  grid-area: divider;
  margin: 0;
  width: 100%;
`

const ContainerVDivider = styled.hr`
  align-self: stretch;
  border: none;
  border-left: 1px solid ${({ theme }) => theme.palette.neutral.default.six};
  display: none;
  flex-shrink: 0;
  grid-area: divider;

  ${mediaQueryUp.tablet} {
    display: block;
    margin: 0;
  }
`

const PricingContainer = styled(Group)`
  grid-area: pricing;

  ${mediaQueryUp.tablet} {
    justify-self: center;
  }
`

const TotalPriceCaption = styled(PriceRowCaption)`
  > * + *::before {
    content: '·';
    margin: 0 ${rem(4)};
    speak-as: bullets;
    speak: none;
  }
`

const ButtonsGroup = styled(Group)`
  grid-area: buttons;
`

const LabelsGroup = styled(Group)`
  &:empty {
    display: none;
  }
`

const MessageContainer = styled(Group)`
  background-color: ${props => props.theme.palette.highlight.secondary.light};
  min-height: ${rem(40)};
  padding: ${rem(8)} ${rem(24)};
`
interface MappedStateProps {
  defaultDepositAmountPercentage: number
}
interface MappedOfferPageStateProps {
  areRoomsSelected?: boolean
  rooms?: Array<App.Occupants>
  offer: Tours.V2Offer
  selectedVariation: Tours.V2OfferVariation
}

interface Props {
  cheapestPurchasableOption: Tours.V2OfferPurchasableOption
  className?: string
  departure: Tours.V2OfferDeparture
  endLocation: string
  isSelected?: boolean
  occupanciesPriceBreakdowns: Map<App.Occupants, Map<Tours.V2OfferPurchasableOption, Tours.V2OfferDeparturePriceBreakdown>>
  startLocation: string
  onDepartureSelect?: (departure: Tours.V2OfferDeparture) => void
  onGuaranteedLabelClick?: () => void
}

function DepartureCard(props: Props & MappedStateProps & MappedOfferPageStateProps) {
  const {
    areRoomsSelected,
    cheapestPurchasableOption,
    className,
    departure,
    endLocation,
    isSelected,
    occupanciesPriceBreakdowns = EmptyMap,
    offer,
    selectedVariation,
    startLocation,
    onDepartureSelect,
    onGuaranteedLabelClick,
    defaultDepositAmountPercentage,
    rooms,
  } = props
  const depositAmountPercentage = getDepositAmountPercentage(offer, defaultDepositAmountPercentage)

  const {
    formattedStartDate,
    startDateWeekday,
    formattedEndDate,
    endDateWeekday,
    daysCount,
  } = useTourV2DepartureDetails(departure)

  const {
    cheapestPurchasableOptionsPriceBreakdowns,
    accommodableRoomTypes,
    cheapestTotalPrice,
  } = useMemo(
    () => {
      const cheapestPurchasableOptionsPriceBreakdowns: [Tours.V2OfferPurchasableOption, Tours.V2OfferDeparturePriceBreakdown][] = []
      const accommodableRoomTypes = new Set<Tours.V2OfferRoomType>()
      let cheapestTotalPrice = 0

      for (const [, purchasableOptionsPriceBreakdowns] of occupanciesPriceBreakdowns) {
        const [cheapestPurchasableOption, cheapestPriceBreakdown] = min<any>(
          Array.from(purchasableOptionsPriceBreakdowns.entries()),
          ([, priceBreakdown]) => priceBreakdown.totalPrice,
        )

        cheapestTotalPrice += cheapestPriceBreakdown.totalPrice
        accommodableRoomTypes.add(cheapestPriceBreakdown.accommodableRoomType)
        cheapestPurchasableOptionsPriceBreakdowns.push([cheapestPurchasableOption, cheapestPriceBreakdown])
      }

      return {
        cheapestPurchasableOptionsPriceBreakdowns,
        accommodableRoomTypes,
        cheapestTotalPrice,
      }
    },
    [occupanciesPriceBreakdowns],
  )

  const {
    hasDiscount,
    pricePerPerson,
    fullPricePerPerson,
    valuedAt,
    spotsLeft,
  } = useTourV2PurchasableOptionDetails(cheapestPurchasableOption) || {}

  const isUnavailable = departure.status !== 'available'
  const roomInventory: Map<string, number> = useTourDepartureRoomInventory(offer, departure.id)

  const isSoldOut = useMemo(() => {
    if (!offer.isLeTour) {
      return false
    }
    return !Array.from(roomInventory.values()).some(inventory => inventory > 0)
  }, [roomInventory, offer])

  const showPricing = useMemo(() => {
    if (!offer.isLeTour && !isUnavailable) {
      return true
    }
    if (offer.isLeTour) {
      const roomsMap = groupBy(rooms, occupancy => occupancy.adults)
      const totalTwinRooms = roomsMap.get(2)?.length ?? 0
      const totalSingleRooms = roomsMap.get(1)?.length ?? 0
      const packageOptionTypes = new Set(Array.from(roomInventory.keys()).map(key => key.split('_')[1]))
      for (const packageOption of packageOptionTypes) {
        const singleRoomInventory = packageOption ? roomInventory.get(`single_${packageOption}`) || 0 : roomInventory.get('single') || 0
        const twinRoomInventory = packageOption ? roomInventory.get(`twin_${packageOption}`) || 0 : roomInventory.get('twin') || 0
        if (singleRoomInventory >= totalSingleRooms && twinRoomInventory >= totalTwinRooms && !isUnavailable) {
          return true
        }
      }
    }
    return false
  }, [isUnavailable, rooms, roomInventory, offer])

  const handleDateSelectButtonClick = useCallback((_e: React.MouseEvent<HTMLButtonElement>) => {
    onDepartureSelect?.(departure)
    fireInteractionEvent(tourv2DepartureSelect(departure.id))
  }, [departure, onDepartureSelect])

  const typographyColor = isUnavailable ? 'neutral-two' : 'neutral-one'
  const currentCurrency = useContext(CurrencyContext)

  const isDepositAllowed = isTourV2DepositAllowed(departure.startDate, cheapestTotalPrice, currentCurrency, offer.depositType, offer.depositThresholds?.numberOfDays)

  const [isUpgradeAvailable, isRoomOptionsAvailable] = useMemo(() => {
    const purchasableOptions = Array.from(occupanciesPriceBreakdowns.values())
      .filter(occupancyOptions => Array.from(occupancyOptions.keys()).length > 1)
      .flatMap<Tours.V2OfferPurchasableOption>(occupancyOptions => Array.from(occupancyOptions.keys()))
    const { hasPackageUpgrade, hasRoomType } = checkPurchasableOptionHasPackageUpgradeOrRoomType(purchasableOptions)
    return [hasPackageUpgrade, hasRoomType]
  }, [occupanciesPriceBreakdowns])

  const departureCtaType = useMemo(() => {
    if (!isUnavailable && !showPricing) return 'limited'
    if (isUnavailable || isSoldOut) return 'soldOut'
  }, [isUnavailable, showPricing, isSoldOut])

  return <Card
    className={cn(className, {
      'is-unavailable': !showPricing,
      'is-selectable': !!onDepartureSelect,
      'is-selected': isSelected,
    })}
  >
    {isSelected && <MessageContainer direction="horizontal" verticalAlign="center" gap={8}>
      <LineSunsetIcon size="M" />
      <BodyText variant="medium" weight="bold">
        You were interested in this departure
      </BodyText>
    </MessageContainer>}

    <CardBody className={cn(className, { 'is-unavailable': !showPricing })}>
      <DetailsGroup direction="vertical" gap={8} tabletGap={16}>
        <LabelsGroup direction="horizontal" gap={4} wrap="wrap">
          {departure.isGuaranteed && <Clickable onClick={onGuaranteedLabelClick}>
            <StatusLabel variant="confirmation">
              Guaranteed departure
            </StatusLabel>
          </Clickable>}
          {!isUnavailable && spotsLeft && spotsLeft > 0 && spotsLeft < 5 && <UrgencyLabel variant="critical2">{`Hurry, only ${pluralizeToString('spot', spotsLeft)} left`}</UrgencyLabel>}
          {isSoldOut && <UrgencyLabel variant="critical2">Sold Out</UrgencyLabel>}
          {isUpgradeAvailable && <PromotionLabel variant="default" icon={<LineRoomUpgradeIcon />}>Upgrades available</PromotionLabel>}
          {isRoomOptionsAvailable && <PromotionLabel variant="default" icon={<LineBedDoubleAltIcon />}>Room options available</PromotionLabel>}
          <TourV2UrgencyTags tags={departure.urgencyTags} />
        </LabelsGroup>
        <JourneyDetailContainer>
          <JourneyDetailStartContainer>
            <Caption colour={typographyColor} variant="large" weight="bold">{startLocation}</Caption>
            <Heading colour={typographyColor} variant="heading5" wrap="no-wrap">{formattedStartDate}</Heading>
            <BodyText colour={typographyColor} variant="small">{startDateWeekday}</BodyText>
          </JourneyDetailStartContainer>

          <JourneyDetailLengthContainer>
            <JourneyDetialContainerHDivider />
            <DaysCountCaption colour={typographyColor} variant="large" weight="bold">{daysCount}</DaysCountCaption>
          </JourneyDetailLengthContainer>

          <JourneyDetailEndContainer>
            <Caption colour={typographyColor} variant="large" weight="bold">{endLocation}</Caption>
            <Heading colour={typographyColor} variant="heading5" wrap="no-wrap">{formattedEndDate}</Heading>
            <BodyText colour={typographyColor} variant="small">{endDateWeekday}</BodyText>
          </JourneyDetailEndContainer>
        </JourneyDetailContainer>
      </DetailsGroup>

      <ContainerVDivider role="separator" />

      <PricingContainer direction="vertical" verticalAlign="center" horizontalAlign="start" gap={4} fullWidth>
        {showPricing && <>
          <LabelsGroup direction="horizontal" gap={4} wrap="wrap">
            {hasDiscount && <TourDiscountLabel />}
            {isDepositAllowed && <SecureWithDepositTagAndModal
              depositAmount={offer.depositAmount}
              depositType={offer.depositType}
              offerType={offer.type}
              depositAmountPercentage={depositAmountPercentage}
              depositThresholds={offer.depositThresholds}
            />}
          </LabelsGroup>
          <div>
            <PriceRowPriceCaption>From</PriceRowPriceCaption>
            <PriceRowPrice
              size="L"
              price={pricePerPerson!}
              saleUnit="person"
              taxesAndFees="inclusive"
            />
            {hasDiscount && <PriceRowSale value={fullPricePerPerson!} />}
            <TotalPriceCaption>
              <span>
                <FormatCurrency value={cheapestTotalPrice} />&nbsp;Total
              </span>
              {accommodableRoomTypes.size === 1 && <span>
                {formatRoomType(accommodableRoomTypes.values().next().value)}
              </span>}
            </TotalPriceCaption>
          </div>
          <PriceRowAgentHubCommission productType={offer.productType} size="M" kind="plain" />
          {!!valuedAt && <PriceRowValueDiscountWithCaption
              size="M"
              value={valuedAt.price}
              discountPercentage={valuedAt.savingPercentage}
            />}
          <PricingBreakdownDropdown
              purchasableOptionsPriceBreakdowns={cheapestPurchasableOptionsPriceBreakdowns}
              totalPrice={cheapestTotalPrice}
              isLeTour={offer.isLeTour}
            />
        </>}
        {departureCtaType && <TourV2DepartureCta
          variant="medium"
          type={departureCtaType}
          offer={offer}
          hasPrivateRequest={selectedVariation?.isPrivateRequest || false}
        />}
      </PricingContainer>

      {showPricing && <ButtonsGroup
        fullHeight
        direction="vertical"
        verticalAlign="end"
        tabletVerticalAlign="center"
      >
        <TextButton
          fit="full-width"
          kind="primary"
          disabled={isUnavailable}
          onClick={handleDateSelectButtonClick}
          data-testid="departure-book-button"
          data-id="departure-book-button"
        >
          {areRoomsSelected && 'Book now'}
          {!areRoomsSelected && 'Select'}
        </TextButton>
      </ButtonsGroup>}
    </CardBody>
  </Card>
}

export default connect<MappedStateProps, undefined, Props, App.State>((state) => ({
  defaultDepositAmountPercentage: getDefaultDepositAmountPercentage(state),
}))(withOfferPageState<MappedOfferPageStateProps>((offerPageState) => ({
  areRoomsSelected: !!offerPageState.rooms,
  rooms: offerPageState.rooms,
  offer: offerPageState.tourV2Offer,
  selectedVariation: offerPageState.selectedTourV2OfferVariation,
}))(DepartureCard))
